import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherPlus, FeatherAlertTriangle } from "@subframe/core";
import { UserListTable } from "../components/UserListTable";
import { useUserManagement } from "../hooks/useUserManagement";
import { UserEntity } from "../../domain/entities/UserEntity";
import { useToast } from "../../../../shared/presentation/components";

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { users, loading, error, fetchUsers, deleteUser } = useUserManagement();
  const { showSuccess, showError } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserEntity | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user: UserEntity) => {
    console.log("Editing user:", user);
    navigate(`/admin/users/edit/${user.id}`);
  };

  const handleCreate = () => {
    navigate("/admin/users/add");
  };

  const handleDeleteClick = (userId: number) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      await deleteUser(userToDelete.id);
      showSuccess("User deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      showError(error.message || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            User Management
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage users, roles, and permissions
          </span>
        </div>
        {/* <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreate}
        >
          Add User
        </Button> */}
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">
            {error.message}
          </span>
        </div>
      )}

      {/* Users Table */}
      <div className="w-full">
        <UserListTable
          users={users}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          loading={loading}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <DialogLayout
        open={deleteDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!deleting) {
            setDeleteDialogOpen(open);
            if (!open) {
              setUserToDelete(null);
            }
          }
        }}
      >
        <div className="flex w-[480px] flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
              <FeatherAlertTriangle className="text-error-600" />
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Delete User
              </span>
              <span className="text-body font-body text-subtext-color">
                Are you sure you want to delete this user?
              </span>
            </div>
          </div>

          {userToDelete && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body-bold font-body-bold text-default-font">
                User Details:
              </span>
              <div className="flex flex-col gap-1 text-body font-body text-subtext-color">
                <span>
                  <strong>Email:</strong> {userToDelete.email}
                </span>
                {userToDelete.userName && (
                  <span>
                    <strong>Username:</strong> {userToDelete.userName}
                  </span>
                )}
                <span>
                  <strong>Role:</strong> {userToDelete.role}
                </span>
              </div>
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-error-600">
              This action cannot be undone.
            </span>
            <span className="text-body font-body text-subtext-color">
              The user will be soft-deleted and removed from all active lists.
            </span>
          </div>

          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              loading={deleting}
            >
              {deleting ? "Deleting..." : "Delete User"}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};
